import { FC, useContext, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../../../context/notificationContext";
import useI18n from "../../../../../../hooks/useTranslations";
import { AdminInstallationType } from "../../../../../../http/vertigrip/types/installationTypes";
import { LadderReinforcement } from "../../../../../../http/vertigrip/types/ladderReinforcements";
import { useLadderReinforcementsApi } from "../../../../../../http/vertigrip/useLadderReinforcements";
import Button from "../../../../../ui/Button";
import Form from "../../../../../ui/Form";
import FormField from "../../../../../ui/FormField";
import FormFieldsContainer from "../../../../../ui/FormFieldsContainer";
import ModalDialog from "../../../../../ui/ModalDialog";
import Numeric from "../../../../../ui/Numeric";
import VisibilityScope, { VisibilityScopeValue } from "../../../../../ui/VisibilityScope";

interface UpdateLadderReinforcementForm {
  installationType: AdminInstallationType | null;
  order: number | null;
  visibility: VisibilityScopeValue;
}

interface EditLadderReinforcementModalDialogProps {
  ladderReinforcement?: LadderReinforcement;
  onClose: () => void;
}

const EditLadderReinforcementModalDialog: FC<EditLadderReinforcementModalDialogProps> = ({
  ladderReinforcement,
  onClose,
}: EditLadderReinforcementModalDialogProps) => {
  const i18n = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useContext(NotificationDispatch);
  const ladderReinforcementApi = useLadderReinforcementsApi();

  return (
    <>
      {!!ladderReinforcement && (
        <ModalDialog onClose={() => onClose()} title={i18n.translation.ladderReinforcements.edit}>
          <Form<UpdateLadderReinforcementForm>
            defaultValues={{
              order: ladderReinforcement?.order ?? 50,
              visibility: ladderReinforcement?.visibility,
            }}
            submitText={i18n.translation.common.save}
            disabled={isSubmitting}
            secondaryButton={
              <Button buttonProps={{ onClick: () => onClose() }} type="secondary">
                {i18n.translation.common.cancel}
              </Button>
            }
            onSubmit={() => (formData) =>
              ladderReinforcementApi
                .updateAdminLadderReinforcement(ladderReinforcement?.id!, {
                  order: formData.order!,
                  installationTypeId: ladderReinforcement?.installationType.id!,
                  visibility: formData.visibility,
                })
                .then(() => {
                  onClose();
                  dispatch(showSuccessNotification(i18n.translation));
                })
                .catch((err) => {
                  dispatch(showErrorNotification(i18n, err));
                  setIsSubmitting(false);
                })
            }
          >
            {({ control, setValue }) => {
              return (
                <FormFieldsContainer fullWidth>
                  <Controller
                    name="visibility"
                    control={control}
                    rules={{ required: true }}
                    render={({ field, fieldState }) => (
                      <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                        {({ labelId }) => (
                          <VisibilityScope
                            {...field}
                            inputId={labelId}
                            onChange={(value) => setValue("visibility", value)}
                          />
                        )}
                      </FormField>
                    )}
                  />
                  <Controller
                    name="order"
                    control={control}
                    rules={{ required: true, min: 1 }}
                    render={({ field, fieldState }) => (
                      <FormField label={i18n.translation.common.order} error={fieldState.error}>
                        {({ labelId, isInvalid }) => (
                          <Numeric
                            {...field}
                            id={labelId}
                            isInvalid={isInvalid}
                            decimalScale={0}
                            allowNegative={false}
                            onBlur={(value) => setValue("order", value)}
                          />
                        )}
                      </FormField>
                    )}
                  />
                </FormFieldsContainer>
              );
            }}
          </Form>
        </ModalDialog>
      )}
    </>
  );
};

export default EditLadderReinforcementModalDialog;
